// Prelaunch easter egg: a button that "tickles" the capybara with a giggle, a wiggle and floating hearts.
class SCapyTickle extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.button = this.querySelector('[data-capy-tickle-button]');
    this.status = this.querySelector('[data-capy-tickle-status]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.button) return;
    // pointerup as well as click so the effect still fires inside preview frames that swallow click.
    let lastRun = 0;
    const run = () => {
      const now = Date.now();
      if (now - lastRun < 250) return;
      lastRun = now;
      this.tickle();
    };
    this.button.addEventListener('click', run, { signal: this.abort.signal });
    this.button.addEventListener('pointerup', run, { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  tickle() {
    try { this.giggle(); } catch (error) { /* sound is optional */ }
    this.wiggle();
    this.hearts();
    if (this.status) {
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = this.dataset.thanks || '';
      });
    }
  }

  // Synthesized "hi-hi-hi" giggle: short bouncy chirps with a little vibrato, rising then falling like a laugh.
  giggle() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    this.audio = this.audio || new AudioContextClass();
    const ctx = this.audio;
    if (ctx.state === 'suspended') ctx.resume();

    const master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);

    const notes = [880, 1046, 988, 1175, 1046, 1318];
    const start = ctx.currentTime + 0.02;
    notes.forEach((freq, index) => {
      const t = start + index * 0.09 + (Math.random() * 0.015);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const vibrato = ctx.createOscillator();
      const vibratoDepth = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq * 0.92, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.08, t + 0.035);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.96, t + 0.075);

      vibrato.frequency.value = 22;
      vibratoDepth.gain.value = freq * 0.025;
      vibrato.connect(vibratoDepth).connect(osc.frequency);

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(index === notes.length - 1 ? 0.9 : 0.7, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + (index === notes.length - 1 ? 0.16 : 0.08));

      osc.connect(gain).connect(master);
      osc.start(t);
      vibrato.start(t);
      osc.stop(t + 0.18);
      vibrato.stop(t + 0.18);
    });
  }

  wiggle() {
    const targets = document.querySelectorAll('[data-capy-tickle-target]');
    targets.forEach((target) => {
      const keyframes = this.reduceMotion.matches
        ? [{ transform: 'scale(1)' }, { transform: 'scale(1.03)' }, { transform: 'scale(1)' }]
        : [
            { transform: 'rotate(0deg) scale(1)' },
            { transform: 'rotate(-7deg) scale(1.05)' },
            { transform: 'rotate(6deg) scale(1.07)' },
            { transform: 'rotate(-5deg) scale(1.05)' },
            { transform: 'rotate(3deg) scale(1.03)' },
            { transform: 'rotate(0deg) scale(1)' },
          ];
      target.animate(keyframes, { duration: 700, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
    });
  }

  // Hearts burst from the capybara when it is on screen, otherwise from the button.
  originPoint() {
    const target = document.querySelector('[data-capy-tickle-target]');
    const targetRect = target?.getBoundingClientRect();
    const visible = targetRect && targetRect.bottom > 0 && targetRect.top < window.innerHeight && targetRect.width > 0;
    if (visible) {
      return { x: targetRect.left + targetRect.width / 2, y: targetRect.top + targetRect.height * 0.35 };
    }
    const rect = this.button.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top };
  }

  hearts() {
    const { x, y } = this.originPoint();
    const count = this.reduceMotion.matches ? 6 : 16;
    const symbols = ['💗', '💕', '🤎', '✨', '💖'];
    for (let i = 0; i < count; i += 1) {
      const heart = document.createElement('span');
      heart.textContent = symbols[i % symbols.length];
      heart.setAttribute('aria-hidden', 'true');
      heart.style.cssText = `position:fixed;left:${x}px;top:${y}px;z-index:2147483000;pointer-events:none;user-select:none;font-size:${22 + Math.random() * 14}px;line-height:1;will-change:transform,opacity;`;
      document.body.append(heart);
      const driftX = (Math.random() - 0.5) * 320;
      const rise = 180 + Math.random() * 280;
      const spin = (Math.random() - 0.5) * 70;
      const keyframes = this.reduceMotion.matches
        ? [
            { transform: `translate(calc(-50% + ${driftX * 0.3}px), -${rise * 0.15}px)`, opacity: 0 },
            { transform: `translate(calc(-50% + ${driftX * 0.3}px), -${rise * 0.2}px)`, opacity: 1, offset: 0.4 },
            { transform: `translate(calc(-50% + ${driftX * 0.3}px), -${rise * 0.25}px)`, opacity: 0 },
          ]
        : [
            { transform: 'translate(-50%, 0) scale(0.4)', opacity: 0 },
            { transform: `translate(calc(-50% + ${driftX * 0.4}px), -${rise * 0.4}px) scale(1.15) rotate(${spin / 2}deg)`, opacity: 1, offset: 0.3 },
            { transform: `translate(calc(-50% + ${driftX}px), -${rise}px) scale(0.9) rotate(${spin}deg)`, opacity: 0 },
          ];
      const animation = heart.animate(keyframes, {
        duration: 1300 + Math.random() * 600,
        delay: i * 45,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      });
      animation.finished.then(() => heart.remove()).catch(() => heart.remove());
    }
  }
}

if (!customElements.get('s-capy-tickle')) customElements.define('s-capy-tickle', SCapyTickle);
